import React, { useContext, useEffect, useMemo, useState } from 'react'

import moment from 'moment'

import { Link } from '@driftt/dds'
import { Button } from '@driftt/tide-core'
import { Icon } from '@driftt/tide-icons'
import { access } from '@driftt/traffic-control'

import { fetchMeetingInsights } from 'api/AccountActivityAPI'
import config from 'config'
import { AccountDrawerContext } from 'context/accountDrawerContext'
import { CurrentUserContext } from 'context/currentUserContext'
import { useAppSelector } from 'hooks/useRedux'
import { Insight, MeetingRecordingBody } from 'modules/accountActivity/domain'
import { ReactComponent as SparklesIcon } from 'svgs/sparkles.svg'
import { contactPath } from 'utils/paths'
import { segmentTrack, TrackType } from 'utils/tracking'

import CardHeader from '../CardHeader'

import { getMentionSummary, MeetingRecordingInsights } from './insights'
import MeetingTranscript from './MeetingTranscript'

import cardStyles from '../styles.module.css'
import styles from './styles.module.css'

interface MeetingRecordingCardProps {
  insight: Insight
}

const MeetingRecordingCard: React.FC<MeetingRecordingCardProps> = ({ insight }) => {
  const [isTranscriptOpen, setIsTranscriptOpen] = useState<boolean>(false)
  const [insights, setInsights] = useState<MeetingRecordingInsights>()

  const orgUsersById = useAppSelector(({ orgUsersById }) => orgUsersById.users)
  const currentUser = useContext(CurrentUserContext)
  const {setIsOpen: setIsDrawerOpen, setAccountId: setSelectedAccountId} = useContext(AccountDrawerContext)
  const hasMeetingRecordingAIHeader =
    access.hasGates('meeting_recording_ai_header') ||
    config.MEETING_RECORDING_AI_WHITELIST?.includes(currentUser?.id)

  const body = insight.body as MeetingRecordingBody

  const participant = body.participants[0]
  const activityDate = moment(insight.lastActivityAt)
  const meetingEvent = body.schedulingEvent
  const meetingTitle =
    body.title ||
    meetingEvent?.zoomInfo?.topic ||
    `${meetingEvent?.meetingType || 'Meeting'} with ${insight.accountData?.name}`

  const entitySummary = useMemo<string[] | undefined>(() => getMentionSummary(insights), [insights])

  useEffect(() => {
    fetchMeetingInsights(body.videoId).then(setInsights)
  }, [body])

  return (
    <>
      <CardHeader insight={insight} />
      <p className={styles.cardDescription}>
        <Link href={contactPath(participant.id)}>
          <span className={cardStyles.linkStyle}>{participant.name || participant.email}</span>
        </Link>
        {participant.role ? ` (${participant.role})` : ''}
        {' from '}
        <Link onClick={() => {
          setIsDrawerOpen(true)
          setSelectedAccountId(insight.accountId)
        }}>
          <span className={cardStyles.linkStyle}>{insight.accountData?.name}</span>
        </Link>
        {' had a meeting with '}
        <span className={styles.userName}>{orgUsersById[body.driftUserId]?.name}</span>
        {` on ${activityDate.format('MMMM D, Y')}`}
        {'.'}
      </p>
      <div className={styles.cardContent}>
        <div className={styles.meetingDetails}>
          <div className={styles.meetingDate}>
            <p className={styles.dayOfMonth}>{activityDate.format('D')}</p>
            <p className={styles.monthAndYear}>{activityDate.format('MMMM Y').toUpperCase()}</p>
          </div>
          <div className={styles.meetingTitleAndSummary}>
            <h2>{meetingTitle}</h2>
            {entitySummary && hasMeetingRecordingAIHeader && (
              <div>
                <div className={styles.mentionsLabel}>
                  <SparklesIcon />
                  <p>MENTIONS</p>
                </div>
                <div className={styles.entityMentions}>
                  {entitySummary.slice(0, 4).map(entity => (
                    <div key={entity} className={styles.entityMention}>
                      {entity}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className={styles.videoPlayerContainer}>
          <iframe
            src={body.videoEmbedLink}
            width="100%"
            height="360px"
            title={body.videoEmbedLink}
          />
          <Button
            className={styles.toggleTranscriptButton}
            onClick={() => {
              setIsTranscriptOpen(!isTranscriptOpen)
              if (!isTranscriptOpen) {
                segmentTrack(TrackType.MEETING_TRANSCRIPT_TOGGLED, {
                  videoId: body.videoId,
                  videoURL: body.videoEmbedLink,
                  insightId: insight.id
                })
              }
            }}
            type="tertiary">
            <p>View meeting transcript</p>
            <div className={styles.toggleMeetingTranscriptIcon}>
              {isTranscriptOpen ? (
                <Icon name="chevron-down" height={10} width={10} />
              ) : (
                <Icon name="arrow-1-right" height={6} width={6} />
              )}
            </div>
          </Button>
          {isTranscriptOpen && <MeetingTranscript videoId={body.videoId} insights={insights} />}
        </div>
      </div>
    </>
  )
}

export default MeetingRecordingCard
